import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, Plus, Pencil, Trash2, Loader2, ExternalLink, FileText } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  name: '', overview: '', white_paper_url: '', website_url: '', publication_id: '',
};

export default function CompaniesPage() {
  const { user }  = useAuth();
  const [pubs, setPubs]           = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedPub, setSelectedPub] = useState('');
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState('');

  const canManage = ['admin', 'editor'].includes(user?.role);

  const fetchPubs = async () => {
    try {
      const res = await api.get('/publications');
      // Only industry publications
      const industry = res.data.filter(p => p.site_type === 'industry');
      setPubs(industry);
      if (industry.length > 0 && !selectedPub) setSelectedPub(industry[0].id);
    } catch { toast.error('Failed to load publications'); }
  };

  const fetchCompanies = async (pubId) => {
    if (!pubId) return;
    setLoading(true);
    try {
      const res = await api.get('/companies', { params: { publication_id: pubId } });
      setCompanies(res.data || []);
    } catch { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPubs(); }, []);
  useEffect(() => { if (selectedPub) fetchCompanies(selectedPub); }, [selectedPub]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, publication_id: selectedPub });
    setDialog(true);
  };

  const openEdit = (company) => {
    setEditing(company);
    setForm({
      name:            company.name            || '',
      overview:        company.overview        || '',
      white_paper_url: company.white_paper_url || '',
      website_url:     company.website_url     || '',
      publication_id:  selectedPub,
    });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/companies/${editing.id}`, form);
        toast.success('Company updated');
      } else {
        await api.post('/companies', form);
        toast.success('Company added');
      }
      setDialog(false);
      fetchCompanies(selectedPub);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (companyId, name) => {
    if (!window.confirm(`Remove "${name}" from this publication's Company A-Z?`)) return;
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success('Company removed');
      fetchCompanies(selectedPub);
    } catch { toast.error('Failed to delete company'); }
  };

  const label = 'text-xs text-[#6E6258] font-medium mb-1 block';
  const selectedPubObj = pubs.find(p => p.id === selectedPub);

  const filtered = companies
    .filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Group A-Z
  const grouped = filtered.reduce((acc, c) => {
    const letter = c.name[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-outfit tracking-tight">Companies</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage the Company A-Z for industry publication sites.</p>
        </div>
        {canManage && selectedPub && (
          <Button onClick={openCreate} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
            <Plus size={16} className="mr-1.5" /> Add Company
          </Button>
        )}
      </div>

      {/* Publication selector */}
      <div className="flex items-end gap-4">
        <div className="w-64">
          <label className="text-sm text-[#6E6258] font-medium mb-1.5 block">Publication</label>
          <Select value={selectedPub} onValueChange={setSelectedPub}>
            <SelectTrigger className="bg-white text-[#1F1A17] border-border">
              <SelectValue placeholder="Select an industry publication..." />
            </SelectTrigger>
            <SelectContent className="bg-white border-border">
              {pubs.map(p => (
                <SelectItem key={p.id} value={p.id} className="text-[#1F1A17]">
                  {p.display_name || p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedPub && (
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search companies..."
            className="max-w-xs bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
          />
        )}
      </div>

      {pubs.length === 0 && !loading ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Briefcase className="mx-auto text-[#E7D2B8] mb-3" size={40} />
            <p className="text-muted-foreground text-lg">No industry publications found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Set a publication's site type to "Industry" in Site Launcher first.
            </p>
          </CardContent>
        </Card>
      ) : !selectedPub ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Briefcase className="mx-auto text-[#E7D2B8] mb-3" size={40} />
            <p className="text-muted-foreground">Select a publication to manage its companies.</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 size={16} className="animate-spin" /> Loading companies...
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Briefcase className="mx-auto text-[#E7D2B8] mb-3" size={40} />
            <p className="text-muted-foreground text-lg">No companies yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add companies to appear in the A-Z page on {selectedPubObj?.display_name || selectedPubObj?.name}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([letter, items]) => (
            <div key={letter}>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-lg font-bold text-foreground font-outfit w-8">{letter}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-2 ml-11">
                {items.map(company => (
                  <Card key={company.id} className="bg-card border-border hover:border-[#C9B49A] transition-all">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-foreground text-sm">{company.name}</p>
                          {company.overview && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{company.overview}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            {company.website_url && (
                              <a
                                href={company.website_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                onClick={e => e.stopPropagation()}
                              >
                                <ExternalLink size={11} /> Website
                              </a>
                            )}
                            {company.white_paper_url && (
                              <a
                                href={company.white_paper_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                onClick={e => e.stopPropagation()}
                              >
                                <FileText size={11} /> White Paper
                              </a>
                            )}
                          </div>
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-1 shrink-0">
                            <Button variant="ghost" size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-blue-600 hover:bg-[#FFF4E8]"
                              onClick={() => openEdit(company)}>
                              <Pencil size={13} />
                            </Button>
                            <Button variant="ghost" size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-red-500 hover:bg-red-50"
                              onClick={() => handleDelete(company.id, company.name)}>
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-outfit">
              {editing ? 'Edit Company' : 'Add Company'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {editing
                ? 'Update the company details.'
                : `Add a company to ${selectedPubObj?.display_name || selectedPubObj?.name}'s Company A-Z.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className={label}>Company Name <span className="text-red-400">*</span></label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Barclays"
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
            </div>
            <div>
              <label className={label}>Overview</label>
              <Textarea value={form.overview} onChange={e => setForm(f => ({ ...f, overview: e.target.value }))}
                placeholder="Brief description of the company and its relevance to this sector..."
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] min-h-[80px] resize-none" />
            </div>
            <div>
              <label className={label}>Website URL</label>
              <Input value={form.website_url} onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))}
                placeholder="https://..."
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
            </div>
            <div>
              <label className={label}>White Paper URL</label>
              <Input value={form.white_paper_url} onChange={e => setForm(f => ({ ...f, white_paper_url: e.target.value }))}
                placeholder="https://..."
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-muted-foreground hover:text-foreground">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
              {saving ? <><Loader2 size={14} className="animate-spin mr-1.5" />Saving...</> : editing ? 'Save Changes' : 'Add Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}